/**
 * Cloudflare CDN Utility
 * Nivel Silicon Valley - CDN optimization para videos e imágenes
 * 
 * Cloudflare (plan gratuito) ofrece:
 * - CDN global con 200+ edge locations
 * - Compresión automática (Brotli/Gzip)
 * - HTTP/2 y HTTP/3
 * - Caché inteligente
 * - DDoS protection
 */

// URLs base
const S3_BASE_URL = 'https://jiddufilms-bucket.s3.us-east-2.amazonaws.com';

// URL de Cloudflare CDN (configurar en .env)
// Si no está configurado, usa S3 directamente
// Formato esperado: https://cdn.jiddufilms.com o https://jiddufilms.com (si el dominio está en Cloudflare)
const CLOUDFLARE_CDN_URL = process.env.REACT_APP_CLOUDFLARE_CDN_URL || null;

// Determinar si estamos usando Cloudflare
const USE_CLOUDFLARE = CLOUDFLARE_CDN_URL !== null && CLOUDFLARE_CDN_URL !== '';

/**
 * Convierte una URL de S3 a URL de Cloudflare CDN
 * Cloudflare debe estar configurado para hacer proxy de S3
 * 
 * @param {string} s3Url - URL completa de S3
 * @returns {string} - URL de Cloudflare CDN o S3 si no está configurado
 */
export const getCloudflareUrl = (s3Url) => {
  if (!s3Url) return null;
  
  // Si Cloudflare no está configurado, retornar URL original
  if (!USE_CLOUDFLARE) {
    return s3Url;
  }
  
  // Si ya es una URL de Cloudflare, retornar tal cual
  if (s3Url.includes(CLOUDFLARE_CDN_URL)) {
    return s3Url;
  }
  
  // Extraer la ruta del archivo de la URL de S3
  // Ejemplo: https://jiddufilms-bucket.s3.us-east-2.amazonaws.com/videos/video.mp4
  // -> /videos/video.mp4
  try {
    const url = new URL(s3Url);
    const path = url.pathname;
    
    // Construir URL de Cloudflare
    // Cloudflare debe estar configurado para hacer proxy de:
    // cdn.jiddufilms.com/videos/* -> jiddufilms-bucket.s3.us-east-2.amazonaws.com/videos/*
    const cloudflareUrl = `${CLOUDFLARE_CDN_URL}${path}`;
    
    return cloudflareUrl;
  } catch (error) {
    console.warn('Error converting S3 URL to Cloudflare URL:', error);
    return s3Url; // Fallback a S3
  }
};

/**
 * Obtiene la URL base para recursos (videos, imágenes)
 * Usa Cloudflare si está configurado, sino S3
 */
export const getBaseUrl = () => {
  return USE_CLOUDFLARE ? CLOUDFLARE_CDN_URL : S3_BASE_URL;
};

/**
 * Genera URL optimizada para video
 * Cloudflare automáticamente comprime y cachea
 * 
 * @param {string} filename - Nombre del archivo de video
 * @param {string} folder - Carpeta (ej: 'videos', 'videos_cover')
 * @returns {string} - URL optimizada
 */
export const getOptimizedVideoUrl = (filename, folder = 'videos') => {
  const baseUrl = getBaseUrl();
  const s3Url = `${S3_BASE_URL}/${folder}/${filename}`;
  
  if (USE_CLOUDFLARE) {
    return getCloudflareUrl(s3Url);
  }
  
  return s3Url;
};

/**
 * Genera URL optimizada para imagen
 * Cloudflare puede optimizar imágenes automáticamente (en planes pagos)
 * Por ahora, solo usamos el CDN para caché y compresión
 * 
 * @param {string} filename - Nombre del archivo de imagen
 * @param {string} folder - Carpeta (ej: 'videos_cover')
 * @returns {string} - URL optimizada
 */
export const getOptimizedImageUrl = (filename, folder = 'videos_cover') => {
  const baseUrl = getBaseUrl();
  const s3Url = `${S3_BASE_URL}/${folder}/${filename}`;
  
  if (USE_CLOUDFLARE) {
    return getCloudflareUrl(s3Url);
  }
  
  return s3Url;
};

/**
 * Verifica si Cloudflare está configurado y activo
 */
export const isCloudflareEnabled = () => {
  return USE_CLOUDFLARE;
};

/**
 * Preload de recursos críticos usando Cloudflare
 * Cloudflare optimiza automáticamente la entrega
 */
export const preloadResource = (url, as = 'video') => {
  if (!url) return;
  
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = as;
  link.href = url;
  
  // Cloudflare respeta estos headers
  if (as === 'video') {
    link.setAttribute('fetchpriority', 'low'); // Videos no críticos
  } else if (as === 'image') {
    link.setAttribute('fetchpriority', 'high'); // Imágenes críticas
  }
  
  document.head.appendChild(link);
  
  // Limpiar después de un tiempo
  setTimeout(() => {
    if (link.parentNode) {
      link.parentNode.removeChild(link);
    }
  }, 30000);
};

export default {
  getCloudflareUrl,
  getBaseUrl,
  getOptimizedVideoUrl,
  getOptimizedImageUrl,
  isCloudflareEnabled,
  preloadResource,
};



